import { useEffect, useCallback, useMemo } from 'react'

interface KeyboardShortcutOptions {
  disabled?: boolean
  preventDefault?: boolean
  /** Ignores shortcut if an input, textarea, or contentEditable is focused. Defaults to `true`. */
  ignoreInputFocus?: boolean
}

interface ParsedShortcut {
  key: string
  metaKey: boolean
  ctrlKey: boolean
  shiftKey: boolean
  altKey: boolean
}

function parseShortcut(
  shortcut: string,
  isMacPlatform: boolean,
): ParsedShortcut {
  const lowerShortcut = shortcut.toLowerCase()
  const parts = lowerShortcut.split('+').map((part) => part.trim())
  const mainKey = parts.pop() || ''

  let meta = parts.includes('meta') || parts.includes('cmd')
  let ctrl = parts.includes('ctrl') || parts.includes('control')
  const shift = parts.includes('shift')
  const alt = parts.includes('alt') || parts.includes('option')

  if (parts.includes('mod')) {
    if (isMacPlatform) {
      meta = true
    } else {
      ctrl = true
    }
  }

  return {
    key: mainKey,
    metaKey: meta,
    ctrlKey: ctrl,
    shiftKey: shift,
    altKey: alt,
  }
}

/**
 * Custom hook to handle keyboard shortcuts globally.
 *
 * @example
 * // Basic usage
 * useKeyboardShortcut('mod+S', handleSave)
 *
 * // With options
 * useKeyboardShortcut('mod+Enter', handleSubmit, {
 *   disabled: isSubmitting,
 *   ignoreInputFocus: false
 * })
 *
 * @param shortcut The keyboard shortcut string (e.g., "mod+S", "Shift+Enter", "Alt+Ctrl+Delete").
 *                 Use "mod" for the platform-agnostic Command/Control key (⌘ on Mac, Ctrl on PC).
 *                 Other modifiers: "Shift", "Alt" (or "Option"), "Ctrl" (for literal Control key),
 *                 "Cmd" (or "Meta" for literal Command/Windows key).
 *                 Key names should generally match `event.key` (case-insensitive for the main key part).
 * @param callback The function to execute when the shortcut is pressed.
 * @param options  Configuration options for the shortcut.
 *   @param options.disabled         If true, disables the shortcut. Default: false.
 *   @param options.preventDefault   If true, calls event.preventDefault() when the shortcut is triggered. Default: true.
 *   @param options.ignoreInputFocus If true, ignores the shortcut if an input, textarea, or contentEditable element is focused. Default: true.
 */
function validateShortcut(shortcut: string): void {
  if (!shortcut || typeof shortcut !== 'string') {
    throw new Error('Shortcut must be a non-empty string')
  }
}

export function useKeyboardShortcut(
  shortcut: string,
  callback: (event: KeyboardEvent) => void,
  options: KeyboardShortcutOptions = {},
): void {
  const {
    disabled = false,
    preventDefault = true,
    ignoreInputFocus = true,
  } = options

  // Platform check at runtime
  const isMacPlatform =
    typeof window !== 'undefined' &&
    /Mac|iPod|iPhone|iPad/.test(window.navigator.platform)

  validateShortcut(shortcut)

  const parsed = useMemo(
    () => parseShortcut(shortcut, isMacPlatform),
    [shortcut, isMacPlatform],
  )

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (disabled) return

      if (ignoreInputFocus) {
        const target = event.target as HTMLElement
        if (
          target &&
          (target.tagName === 'INPUT' ||
            target.tagName === 'TEXTAREA' ||
            target.isContentEditable)
        ) {
          return
        }
      }

      const eventKey = event.key.toLowerCase()

      if (
        eventKey === parsed.key &&
        event.metaKey === parsed.metaKey &&
        event.ctrlKey === parsed.ctrlKey &&
        event.shiftKey === parsed.shiftKey &&
        event.altKey === parsed.altKey
      ) {
        if (preventDefault) {
          event.preventDefault()
        }
        callback(event)
      }
    },
    [callback, disabled, preventDefault, ignoreInputFocus, parsed],
  )

  useEffect(() => {
    if (disabled) {
      window.removeEventListener('keydown', handleKeyDown)
      return
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown, disabled])
}
